"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.focusable = focusable;
exports.modalDepth = modalDepth;
exports.useModalSurface = useModalSurface;
exports.useDismiss = useDismiss;
exports.anchorTo = anchorTo;
exports.useAnchorRect = useAnchorRect;
const react_1 = require("react");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The three behaviours every overlay in this package shares.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `useModalSurface` for anything that takes over the screen (dialog, sheet, palette),
 * `useDismiss` for anything that hangs off a trigger (menu, picker popover), and `anchorTo`
 * for placing the second kind.
 *
 * They live together because the difference between them is a decision, not an accident:
 *
 * - a **modal** traps focus, locks scroll, and restores focus to whatever opened it. Escape
 *   closes it. Tab cycles inside it.
 * - a **dismissable** does none of that. It closes on Escape, on an outside pointer-down and
 *   on Tab — because tabbing out of a menu should leave the menu, not cycle inside it.
 *
 * Trapping focus in a dropdown is the classic over-application: it makes the rest of the page
 * unreachable for a control that is meant to be a shortcut.
 */
/**
 * Every element inside `root` that a keyboard can reach, in document order.
 *
 * ⚠️ **The visibility filter is `checkVisibility()`, and the reason is jsdom.** The obvious
 * filter is `offsetParent !== null`, and it is a trap here: **jsdom does no layout, so it
 * reports `offsetParent` as `null` for every element**. A focus trap built on it finds nothing
 * under test — and a trap over an empty list does nothing while every assertion about "Escape
 * closes it" still passes, so the suite reports green about a trap that is not trapping.
 *
 * `checkVisibility()` is absent in jsdom rather than lying, so the optional call skips the
 * refinement under test and applies it in a browser. `src/__tests__/overlay.test.ts` asserts
 * this list is non-empty for a populated surface, which is the assertion the `offsetParent`
 * version fails.
 */
function focusable(root) {
    if (root === null)
        return [];
    const candidates = root.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), ' +
        'select:not([disabled]), [tabindex]:not([tabindex="-1"])');
    return [...candidates].filter((element) => {
        if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') {
            return false;
        }
        /* ⚠️ Read through a deliberately-optional local. The DOM lib types declare
         * `checkVisibility()` as always present and returning `boolean`, so `?.` and `?? true`
         * written inline are `no-unnecessary-condition` errors — the types say the guard is dead
         * code. It is not: **jsdom does not implement the method at all**, so in the test
         * environment the call would throw. The types describe a browser; the guard is for the one
         * environment that is not one. */
        const check = element.checkVisibility;
        return check === undefined ? true : check.call(element);
    });
}
const layers = [];
function onTop(token) {
    return layers[layers.length - 1]?.token === token;
}
function topModal() {
    return layers.filter((layer) => layer.modal).at(-1)?.token;
}
/** Spliced by identity — `useModalSurface`'s cleanup argues why never `pop()`. */
function leave(token) {
    const at = layers.findIndex((layer) => layer.token === token);
    if (at !== -1)
        layers.splice(at, 1);
}
/** Exposed for the test that proves the stack is what orders Escape, not listener order. */
function modalDepth() {
    return layers.filter((layer) => layer.modal).length;
}
/**
 * Focus trap, focus restore, Escape, scroll lock — for the duration `open` is true.
 *
 * ⚠️ **Focus restore is the half that gets forgotten**, and it is the half a keyboard user
 * notices: closing a dialog without it drops focus to `<body>`, so the next Tab starts from the
 * top of the page. The element that had focus when the overlay opened is captured and refocused
 * on close.
 *
 * ⚠️ **Nested overlays: only the top layer answers Escape.** A confirm dialog opened from inside a
 * sheet must close the confirm and leave the sheet standing — and a menu opened inside either must
 * close the menu alone. Without that the user sees their whole context vanish because they
 * cancelled a sub-step.
 *
 * ⚠️ **Escape is heard in the BUBBLE phase, and one already handled is left alone** (28 Oct 2026).
 * A cell being edited inside a dialog takes Escape to put its value back, and calls
 * `preventDefault` to say so; the dialog, hearing it last, stays open. Tab stays in the capture
 * phase, where the trap has always been.
 */
function useModalSurface(open, onClose) {
    const ref = (0, react_1.useRef)(null);
    const restoreTo = (0, react_1.useRef)(null);
    const [nested, setNested] = (0, react_1.useState)(false);
    /* Held in a ref so the effect does not re-run — and re-focus — on every parent render. */
    const close = (0, react_1.useRef)(onClose);
    close.current = onClose;
    (0, react_1.useEffect)(() => {
        if (!open)
            return;
        /* ⚠️ Read BEFORE the push, so *"is anything already open"* is the question being asked — and
           what it decides now is how DARK this surface's scrim is, not whether it draws one. */
        setNested(modalDepth() > 0);
        /* Pushed before the listener is attached, so `token === last` is already true by the time
         * any key can arrive. */
        const token = Symbol('modal');
        layers.push({ token, modal: true });
        restoreTo.current = document.activeElement;
        const surface = ref.current;
        (focusable(surface)[0] ?? surface)?.focus();
        const onKeyDown = (event) => {
            /* ⚠️ Only the topmost MODAL traps Tab (Escape is `onEscape`'s). Letting a buried overlay
             * keep trapping Tab is the subtler half: a confirm dialog over a sheet would have its
             * focus yanked back into the sheet on every Tab, so the confirm's own buttons become
             * unreachable by keyboard while looking perfectly normal. */
            if (event.key !== 'Tab' || topModal() !== token)
                return;
            const reachable = focusable(ref.current);
            if (reachable.length === 0)
                return;
            const index = reachable.indexOf(document.activeElement);
            const first = reachable[0];
            const last = reachable[reachable.length - 1];
            if (event.shiftKey && index <= 0) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && index === reachable.length - 1) {
                event.preventDefault();
                first.focus();
            }
        };
        const onEscape = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented || !onTop(token))
                return;
            close.current();
        };
        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('keydown', onEscape);
        /* ⚠️ The PREVIOUS value, not `''`. Two overlays open at once both restore on unmount, and
         * the inner one clearing the lock outright would let the page behind the outer one scroll. */
        const scrollWas = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('keydown', onEscape);
            document.body.style.overflow = scrollWas;
            /* ⚠️ Spliced by identity, not popped. Overlays do not always close in the order they
             * opened — a route change can unmount an outer sheet while a confirm is still up — and
             * `pop()` would then remove somebody else's token and leave a dead one on top, at which
             * point Escape stops working entirely and nothing says why. */
            leave(token);
            const target = restoreTo.current;
            if (target instanceof HTMLElement)
                target.focus();
        };
    }, [open]);
    return { ref, nested };
}
/**
 * Escape, outside pointer-down and Tab close it. No trap, no scroll lock.
 *
 * ⚠️ **`mousedown`, not `click`.** A menu that closes on `click` closes on mouse-UP, so
 * pressing on a menu item and releasing outside it still fires the item. `mousedown` also
 * beats a `click` handler elsewhere on the page, which is what "dismiss and do nothing else"
 * should mean.
 *
 * ⚠️ **The trigger is excluded from "outside".** Without that, clicking a trigger while its
 * menu is open runs dismiss AND the trigger's toggle, so the menu closes and immediately
 * reopens — a control that cannot be closed by the button that opened it.
 */
function useDismiss(open, onDismiss, 
/**
 * 29 Oct 2026 · `closeOnTab: false` for a surface people work INSIDE — a pop-over of controls,
 * where Tab moves between them. A menu keeps the default: Tab leaving it is leaving it.
 */
{ closeOnTab = true } = {}) {
    const surface = (0, react_1.useRef)(null);
    const trigger = (0, react_1.useRef)(null);
    const dismiss = (0, react_1.useRef)(onDismiss);
    dismiss.current = onDismiss;
    (0, react_1.useEffect)(() => {
        if (!open)
            return;
        /* ⚠️ On the overlay stack, so the dialog it was opened in leaves this Escape to it — and a
           picker opened from a filter panel closes before the panel does. */
        const token = Symbol('dismissable');
        layers.push({ token, modal: false });
        const onPointerDown = (event) => {
            const target = event.target;
            if (target === null)
                return;
            /* ⚠️ 29 Oct 2026 · only the TOP layer takes an outside click. A picker opened inside a
               pop-over draws its list in a portal — outside the pop-over's box — so a pick in it used to
               close the pop-over too. The picker answers; the pop-over waits for one of its own. */
            if (!onTop(token))
                return;
            if (surface.current?.contains(target) === true)
                return;
            if (trigger.current?.contains(target) === true)
                return;
            dismiss.current();
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (!onTop(token))
                    return;
                /* `preventDefault` as well: the dialog hears Escape last and skips one already handled. */
                event.preventDefault();
                event.stopPropagation();
                dismiss.current();
            }
            else if (event.key === 'Tab' && closeOnTab) {
                dismiss.current();
            }
        };
        document.addEventListener('mousedown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown, true);
            leave(token);
        };
    }, [open, closeOnTab]);
    return { surface, trigger };
}
/**
 * ⚠️ **What a surface is assumed to need before it is measured**, in px. Every list in this
 * library caps at `max-h-60` — 240 — so this is the real ceiling rather than a guess, and the
 * flip has to be decided before the surface exists to measure.
 */
const ASSUMED_HEIGHT = 240;
/** A fixed-position style that puts a `width`-wide surface beside `rect`, kept on screen. */
function anchorTo(rect, { side = 'bottom', align = 'start', width, offset = 6 }) {
    /* ⚠️ 17 Oct 2026 · never wider than the screen less its margins — a 300px menu on a 360px phone
       ran past the left edge (the My skills picker). */
    const fitted = typeof window === 'undefined' ? width : Math.min(width, Math.max(160, window.innerWidth - 16));
    const style = { position: 'fixed', width: fitted, zIndex: 60 };
    if (rect === null)
        return style;
    if (side === 'left' || side === 'right')
        return beside(style, rect, side, align, fitted, offset);
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ IT FLIPS WHEN THERE IS NO ROOM BELOW. REPORTED ON THE PAGER.         │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * Every dropdown in the product opened downwards, which is right for a trigger in a toolbar and
     * useless for one in a FOOTER: the pager sits at the bottom of the window by construction, so its
     * page-jump opened off the bottom of the screen with nothing to scroll to — `overflow: hidden` on
     * the shell, `position: fixed` on the surface. *"It needs to open up, not down, since it's at the
     * bottom of the screen."*
     *
     * ⚠️⚠️ **FIXED HERE RATHER THAN AT THE PAGER, BECAUSE THE PAGER IS NOT THE CASE — IT IS AN
     * INSTANCE OF THE CASE.** A row menu on the last row of a table, a cell editor at the bottom of a
     * grid and a filter panel on a short window all do this; giving the pager a `side="top"` would
     * fix one of four and leave the rule where a reader cannot find it.
     *
     * ⚠️ **It flips only when below is genuinely worse.** `below < needed && above > below` — so a
     * trigger in the middle of the page keeps opening downwards, which is what people expect, and one
     * with 100px under it and 600px over it turns around.
     */
    const below = window.innerHeight - rect.bottom - offset;
    const above = rect.top - offset;
    const flipped = side === 'bottom' && below < ASSUMED_HEIGHT && above > below;
    /* ⚠️ The corner nearest the trigger, so `.pop-in` grows the surface out of the control that
       opened it — and a flipped one grows upwards, from the edge that is now touching it. */
    const upward = side === 'top' || flipped;
    style.transformOrigin = `${align === 'end' ? 'right' : 'left'} ${upward ? 'bottom' : 'top'}`;
    if (upward) {
        style.bottom = `${String(window.innerHeight - rect.top + offset)}px`;
        /* ⚠️ And the surface is capped to the space it actually has, so a flipped list scrolls rather
           than running off the TOP — which is the same bug in the other direction. */
        style.maxHeight = `${String(Math.max(120, above))}px`;
    }
    else {
        style.top = `${String(rect.bottom + offset)}px`;
        style.maxHeight = `${String(Math.max(120, below))}px`;
    }
    /* ⚠️ Clamped to the viewport at BOTH ends, and the 8px margin is not decoration: a menu
     * opened from a trigger near the right edge otherwise renders half off-screen, and there is
     * no horizontal scroll to reach it because the shell sets `overflow: hidden`. */
    if (align === 'end') {
        /* ⚠️ And kept off the LEFT edge too: right-aligned to its trigger, a wide menu used to start
           before the screen did. */
        const right = Math.max(8, window.innerWidth - rect.right);
        style.right = `${String(Math.min(right, Math.max(8, window.innerWidth - fitted - 8)))}px`;
    }
    else {
        style.left = `${String(Math.max(8, Math.min(rect.left, window.innerWidth - fitted - 8)))}px`;
    }
    return style;
}
/**
 * Beside the trigger rather than under it: `left` or `right`, flipped when that side has no room
 * and the other has more. `align` is vertical here — `start` lines the surface up with the
 * trigger's top, `end` with its bottom — and the surface is kept inside the window either way.
 */
function beside(style, rect, side, align, width, offset) {
    const roomRight = window.innerWidth - rect.right - offset;
    const roomLeft = rect.left - offset;
    const right = side === 'right'
        ? roomRight >= width || roomRight >= roomLeft
        : roomLeft < width && roomRight > roomLeft;
    style.left = right
        ? `${String(Math.min(rect.right + offset, window.innerWidth - width - 8))}px`
        : `${String(Math.max(8, rect.left - offset - width))}px`;
    const room = window.innerHeight - 16;
    if (align === 'end') {
        style.bottom = `${String(Math.max(8, window.innerHeight - rect.bottom))}px`;
    }
    else {
        style.top = `${String(Math.max(8, Math.min(rect.top, window.innerHeight - ASSUMED_HEIGHT - 8)))}px`;
    }
    style.maxHeight = `${String(room)}px`;
    style.transformOrigin = `${right ? 'left' : 'right'} ${align === 'end' ? 'bottom' : 'top'}`;
    return style;
}
/**
 * The trigger's rect, re-measured while `open` — on scroll and on resize.
 *
 * ⚠️ **`scroll` is captured, because a scroll inside an ancestor does not bubble.** The shell
 * puts every screen in its own scroll region, so a menu anchored on the document's scroll
 * event alone stays put while the row it belongs to moves out from under it.
 */
function useAnchorRect(open, trigger) {
    const [rect, setRect] = (0, react_1.useState)(null);
    const measure = (0, react_1.useCallback)(() => {
        const element = trigger.current;
        if (element !== null)
            setRect(element.getBoundingClientRect());
    }, [trigger]);
    /**
     * ⚠️⚠️ **`useLayoutEffect`, AND `useEffect` HERE MEANT THE MENU PAINTED ONCE IN THE WRONG
     * PLACE.** `anchorTo(null, …)` returns `position: fixed` with no `top` or `left`, because there
     * is nothing yet to anchor to - and a fixed element with no offsets keeps its STATIC position,
     * which for a portal appended to `<body>` is below everything already in the document. So the
     * first frame after a click drew the list somewhere off-screen and the second frame moved it
     * home. On a short list near the foot of a page that reads exactly like a control that does
     * nothing when you click it.
     *
     * ⚠️ A layout effect runs after the DOM is written and before the browser paints, so the
     * measured position is in place for the first frame there is one.
     */
    (0, react_1.useLayoutEffect)(() => {
        if (!open)
            return;
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [open, measure]);
    return { rect, measure };
}
//# sourceMappingURL=overlay.js.map